import dayjsTZ from "../../../../utils/dayjs";

/** programFields: Politika e oreve jashte orarit */
export const OFF_SHIFT_POLICIES_FIELD_ID =
  "3d077179-a545-43ec-98be-7e1bf71b09aa";

/** Minimum stripped note length when grace/late requires a reason */
export const MIN_NOTE_CHARS = 5;

export const MIN_NOTE_HINT = `Të paktën ${MIN_NOTE_CHARS} karaktere.`;

const BYPASS_ROLES = new Set([
  "Admin",
  "Super Admin",
  "Supervisor",
  "Branch Manager",
]);

/**
 * Managers may edit start/end outside employee grace on Veprime Turni.
 * @param {string|undefined|null} userRole
 */
export function canBypassGrace(userRole) {
  return BYPASS_ROLES.has(userRole);
}

/**
 * Read Grace Period minutes from programFields.
 * @param {Array|undefined} programFields
 * @returns {number}
 */
export function getGracePeriodMinutes(programFields) {
  const raw =
    programFields
      ?.find((el) => el.fieldId === OFF_SHIFT_POLICIES_FIELD_ID)
      ?.fieldOptions?.find((el) => el.name === "Grace Period")?.formula ?? 0;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Strip HTML and whitespace for note length checks.
 * @param {string} htmlOrText
 * @returns {number}
 */
export function noteCharCount(htmlOrText) {
  if (!htmlOrText) return 0;
  const text = String(htmlOrText)
    .replace(/<[^>]+>/g, "")
    .replace(/\s/g, "");
  return text.length;
}

/**
 * @param {string} htmlOrText
 * @returns {boolean} true when note satisfies MIN_NOTE_CHARS
 */
export function noteMeetsMinLength(htmlOrText) {
  return noteCharCount(htmlOrText) >= MIN_NOTE_CHARS;
}

/**
 * Evaluate whether a chosen past time is within the grace window.
 * Diff is "now − date" in minutes (positive when date is in the past).
 *
 * @param {import('dayjs').Dayjs|number|string|Date} date
 * @param {number} gracePeriodMinutes
 * @param {{ bypass?: boolean }} [options]
 * @returns {{ allowed: boolean, noteRequired: boolean, text: string, bypassed?: boolean }}
 */
export function evaluateGracePeriod(date, gracePeriodMinutes, options = {}) {
  const minutesAgo = dayjsTZ().diff(dayjsTZ(date), "minutes", true);

  if (minutesAgo < 0) {
    return { allowed: false, noteRequired: false, text: "" };
  }

  if (options.bypass) {
    if (minutesAgo < 1) {
      return {
        allowed: true,
        noteRequired: false,
        text: "",
        bypassed: true,
      };
    }
    return {
      allowed: true,
      noteRequired: true,
      text: `Ndryshim jashtë rregullave standarde (rol menaxheri). Nevojitet shënim! (${MIN_NOTE_HINT})`,
      bypassed: true,
    };
  }

  const grace = Number(gracePeriodMinutes) || 0;

  if (grace <= 0) {
    return { allowed: false, noteRequired: false, text: "" };
  }

  if (minutesAgo <= grace) {
    if (minutesAgo < 1) {
      return { allowed: true, noteRequired: false, text: "" };
    }
    return {
      allowed: true,
      noteRequired: true,
      text: `Nevojitet një shënim për këtë ndryshim! (${MIN_NOTE_HINT})`,
    };
  }

  return { allowed: false, noteRequired: false, text: "" };
}

/**
 * Alert copy when grace editing is enabled for start or end.
 * @param {"in"|"out"} kind
 * @param {number} gracePeriodMinutes
 * @param {boolean} [bypass]
 */
export function graceInfoMessage(kind, gracePeriodMinutes, bypass = false) {
  if (bypass) {
    return kind === "out"
      ? `Si menaxher mund të ndryshoni orën e mbarimit; shënimi kërkohet për ndryshime. (${MIN_NOTE_HINT})`
      : `Si menaxher mund të ndryshoni orën e fillimit; shënimi kërkohet për ndryshime. (${MIN_NOTE_HINT})`;
  }
  const mins = Number(gracePeriodMinutes) || 0;
  if (mins <= 0) return null;
  if (kind === "out") {
    return `Mund të ndryshoni orarin e mbarimit deri në ${mins} minuta para, me arsye të vlefshme (${MIN_NOTE_HINT}).`;
  }
  return `Mund të ndryshoni orarin e fillimit deri në ${mins} minuta para, vetëm me arsye të vlefshme (${MIN_NOTE_HINT}).`;
}

export function lateNoteWarningText() {
  return `Nevojitet një shënim i vlefshëm për këtë vonesë! (${MIN_NOTE_HINT})`;
}

/**
 * Clock-in in the overnight window (00:00–05:59) is unusual and must
 * require a reason note — the legacy 10:01 / 13:30 anchors never fire
 * before 10:00, so past-midnight clock-ins slipped through without verification.
 */
export const OVERNIGHT_LATE_BEFORE_HOUR = 6;

/**
 * Whether "now" (or a chosen clock-in time) counts as late and needs a note.
 *
 * @param {import('dayjs').Dayjs|number|string|Date} [now]
 * @param {{ start?: import('dayjs').Dayjs|number|string|Date }|null} [plannedShift]
 * @returns {boolean}
 */
export function isLateClockIn(now = dayjsTZ(), plannedShift = null) {
  const nowDj = dayjsTZ(now);

  if (plannedShift?.start) {
    const startPlus30s = dayjsTZ(plannedShift.start).add(30, "second");
    return nowDj.diff(startPlus30s, "minutes", true) > 1;
  }

  // No planned shift: overnight hours always need a reason.
  if (nowDj.hour() < OVERNIGHT_LATE_BEFORE_HOUR) {
    return true;
  }

  const afternoonAnchor = dayjsTZ(nowDj)
    .set("hour", 13)
    .set("minute", 30)
    .set("second", 0);
  const morningAnchor = dayjsTZ(nowDj)
    .set("hour", 10)
    .set("minute", 1)
    .set("second", 30);
  const time1PM = dayjsTZ(nowDj).set("hour", 13).set("minute", 0);

  if (nowDj.diff(afternoonAnchor, "minutes", true) > 1) {
    return true;
  }
  if (
    nowDj.diff(morningAnchor, "minutes", true) > 1 &&
    nowDj.diff(time1PM, "minutes", true) < 1
  ) {
    return true;
  }
  return false;
}

export function overnightLateNoteWarningText() {
  return `Fillimi pas mesnatës kërkon arsye (turn i vonë / jashtë orarit). (${MIN_NOTE_HINT})`;
}
